import { ApiService } from '@/apiConnection/ApiService';
import { Urls } from '@/apiConnection/urls';
import { CustomDeleteComponent } from '@/components/custom-delete/custom-delete.component';
import { CentToEurDisplayDirective } from '@/directives/cent-to-eur-display.directive';
import { DecimalInputDirective } from '@/directives/decimal-input.directive';
import { Utils } from '@/utils/utils';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SAUModalService } from '@some-angular-utils/modal';

/**
 * Variaciones (estilo WooCommerce) de un producto: cada variación es un producto normal con
 * `parent_id`, así que líneas de venta/compra/ticket, stock e inventario funcionan igual.
 * Los atributos y términos salen del tipo de producto *guardado* del padre, que es lo que
 * usa el backend al generarlas (ProductVariationService::generate).
 */
@Component({
  selector: 'app-product-variations',
  imports: [FormsModule, CentToEurDisplayDirective, DecimalInputDirective],
  templateUrl: './product-variations.component.html',
})
export class ProductVariationsComponent implements OnInit {
  @Input({ required: true }) productId!: number;
  @Input() productTypeId: number | null = null;
  /** Fires after variations are created/deleted, so the parent form can refresh has_variations. */
  @Output() changed = new EventEmitter<void>();

  private sauModalService = inject(SAUModalService);

  public loading = signal(false);
  public attributes: any[] = [];
  public variations: any[] = [];
  public selectedTermIds = new Set<number>();
  public selectedVariationIds = new Set<number>();
  public bulk: { price_ticket: number | null; cost: number | null; stock: number | null } = { price_ticket: null, cost: null, stock: null };

  constructor(
    private apiService: ApiService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.loadAttributes();
    this.loadVariations();
  }

  get totalStock(): number {
    return this.variations.reduce((sum, variation) => sum + Number(variation.stock || 0), 0);
  }

  /** Cartesian product size of the selected terms (attributes with no term selected don't count). */
  get combinationsCount(): number {
    const perAttribute = this.attributes
      .map(attribute => attribute.terms.filter((term: any) => this.selectedTermIds.has(term.id)).length)
      .filter(count => count > 0);
    return perAttribute.length ? perAttribute.reduce((total, count) => total * count, 1) : 0;
  }

  loadAttributes() {
    if (!this.productTypeId) {
      this.attributes = [];
      return;
    }

    this.apiService.getPetition(`${Urls.urls.productTypes}/${this.productTypeId}/attributes`).subscribe({
      next: (data: any) => {
        this.attributes = data.list || [];
        this.cdr.detectChanges();
      },
    });
  }

  loadVariations() {
    this.loading.set(true);
    this.apiService.getPetition(`${Urls.urls.products}/${this.productId}/variations`).subscribe({
      next: (data: any) => {
        this.variations = data.list || [];
        this.selectedVariationIds.clear();
        this.loading.set(false);
        this.cdr.detectChanges();
      },
      error: () => this.loading.set(false),
    });
  }

  toggleTerm(termId: number) {
    this.selectedTermIds.has(termId) ? this.selectedTermIds.delete(termId) : this.selectedTermIds.add(termId);
  }

  toggleVariation(id: number) {
    this.selectedVariationIds.has(id) ? this.selectedVariationIds.delete(id) : this.selectedVariationIds.add(id);
  }

  toggleAllVariations() {
    if (this.selectedVariationIds.size === this.variations.length) {
      this.selectedVariationIds.clear();
    } else {
      this.variations.forEach(variation => this.selectedVariationIds.add(variation.id));
    }
  }

  termLabel(variation: any): string {
    return (variation.variation_terms || [])
      .map((term: any) => `${term.attribute?.name ?? ''}: ${term.value}`)
      .join(' · ');
  }

  generate() {
    if (!this.selectedTermIds.size) {
      Utils.notification.alert('Selecciona al menos un valor de algún atributo');
      return;
    }

    this.loading.set(true);
    this.apiService.post(`${Urls.urls.products}/${this.productId}/variations/generate`, { term_ids: [...this.selectedTermIds] }).subscribe({
      next: (response: any) => {
        Utils.notification.success(response?.message || 'Variaciones creadas');
        this.selectedTermIds.clear();
        this.loadVariations();
        this.changed.emit();
      },
      error: (error: any) => {
        this.loading.set(false);
        Utils.notification.alert(Utils.extractErrorMessage(error) || 'Error al generar las variaciones');
      },
    });
  }

  /** Applies the filled bulk fields to the selected variations (all of them if none is selected). Euros in the UI, cents to the backend. */
  applyBulk() {
    const body: any = {};
    if (this.bulk.price_ticket != null) body.price_ticket = Math.round(this.bulk.price_ticket * 100);
    if (this.bulk.cost != null) body.cost = Math.round(this.bulk.cost * 100);
    if (this.bulk.stock != null) body.stock = this.bulk.stock;

    if (!Object.keys(body).length) {
      Utils.notification.alert('Indica algún valor para aplicar');
      return;
    }

    if (this.selectedVariationIds.size) {
      body.ids = [...this.selectedVariationIds];
    }

    this.loading.set(true);
    this.apiService.put(`${Urls.urls.products}/${this.productId}/variations/bulk`, body).subscribe({
      next: (response: any) => {
        Utils.notification.success(`Variaciones actualizadas: ${response?.updated ?? ''}`);
        this.bulk = { price_ticket: null, cost: null, stock: null };
        this.loadVariations();
      },
      error: (error: any) => {
        this.loading.set(false);
        Utils.notification.alert(Utils.extractErrorMessage(error) || 'Error al actualizar las variaciones');
      },
    });
  }

  async openEditForm(variation: any) {
    // Lazy import: ProductFormComponent embeds this component, a static import would be circular.
    const { ProductFormComponent } = await import('../product-form/product-form.component');
    const modalRef = this.sauModalService.open(ProductFormComponent, {});
    modalRef.componentInstance.id = variation.id;

    modalRef.componentInstance.acceptFunction.subscribe(() => this.loadVariations());
    modalRef.componentInstance.closeFunction.subscribe(() => modalRef.dismiss());
  }

  openDeleteForm(variation: any) {
    const modalRef = this.sauModalService.open(CustomDeleteComponent, {});
    modalRef.componentInstance.id = variation.id;
    modalRef.componentInstance.url = Urls.urls.products;
    modalRef.componentInstance.item = 'variación';

    modalRef.componentInstance.acceptFunction.subscribe(() => {
      this.loadVariations();
      this.changed.emit();
    });
    modalRef.componentInstance.closeFunction.subscribe(() => modalRef.dismiss());
  }
}
